import React, { useState, useEffect, useRef } from 'react';

const EmailInput = ({ 
  id, 
  name = 'email', 
  label = 'Email', 
    hideLabel = false, 
  value = '', 
  onChange, 
  onBlur,
  error, 
  required = false,
  autocomplete = 'email',
  checkDuplicates = false,
  eventId = null,
  blogId = null,
  existingEmails = [],
  skipDuplicateCheck = false,
  onValidationChange = null
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const checkTimeoutRef = useRef(null);
  
  // Clear duplicate error when value changes
  useEffect(() => {
    if (duplicateError) {
      setDuplicateError(null);
    }
  }, [value]);
  
  // Re-check email when checkDuplicates changes (e.g., when bookingFor changes)
  useEffect(() => {
    if (checkDuplicates && value) {
      checkEmailAvailability(value);
    } else if (!checkDuplicates) {
      setDuplicateError(null);
    }
  }, [checkDuplicates]);
  
  const checkEmailAvailability = async (email) => {
    if (!checkDuplicates || !eventId || skipDuplicateCheck || !email) {
      return;
    }
    
    // Validate email format before making API call
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return; // Don't check invalid emails
    }
    
    const emailLower = email.toLowerCase();
    
    // First check against existing emails in the form
    if (existingEmails && existingEmails.includes(emailLower)) {
      setDuplicateError('This email address is already being used by another attendee in this booking.');
      if (onValidationChange) {
        onValidationChange({ isChecking: false, hasError: true });
      }
      return;
    }
    
    setIsChecking(true);
    setDuplicateError(null);
    if (onValidationChange) {
      onValidationChange({ isChecking: true, hasError: false });
    }
    
    try {
      const response = await fetch('/wp-json/lde/v1/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          event_id: eventId,
          blog_id: blogId
        })
      });
      
      const data = await response.json();
      
      // Check the correct response structure
      if (data.success && !data.available) {
        setDuplicateError(data.message || 'This email address already has a booking for this event.');
        if (onValidationChange) {
          onValidationChange({ isChecking: false, hasError: true });
        }
      } else if (!data.success && data.message) {
        // Handle API errors (like invalid email format)
        console.error('Email check error:', data.message);
        if (onValidationChange) {
          onValidationChange({ isChecking: false, hasError: false });
        }
      } else {
        if (onValidationChange) {
          onValidationChange({ isChecking: false, hasError: false });
        }
      }
    } catch (error) {
      console.error('Error checking email availability:', error);
      // Don't show error to user for API failures
      if (onValidationChange) {
        onValidationChange({ isChecking: false, hasError: false });
      }
    } finally {
      setIsChecking(false);
    }
  };
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Clear timeout if user is still typing
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    // Call parent onChange
    if (onChange) {
      onChange(e);
    }
    
    // Debounce email checking on change (wait 1000ms after user stops typing)
    // Only check if email appears to be valid
    if (checkDuplicates && newValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(newValue)) {
        checkTimeoutRef.current = setTimeout(() => {
          checkEmailAvailability(newValue);
        }, 1000);
      }
    }
  };
  
  const handleBlur = (e) => {
    // Clear any pending timeout
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    // Call parent onBlur
    if (onBlur) {
      onBlur(e);
    }
    
    // Check email immediately on blur (only if valid)
    if (checkDuplicates && e.target.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(e.target.value)) {
        checkEmailAvailability(e.target.value);
      }
    }
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, []);
  
  // Determine if there's an error to show
  const hasError = error || duplicateError;
    const errorMessage = duplicateError || (error && error.message);
  const isCheckingFromParent = error && error.isChecking;
  const showCheckingState = isChecking || isCheckingFromParent;

    const renderLabel = () => {
        if (hideLabel || label === '') return null;
        return (
            <label htmlFor={id}>
                {label}
                {required && <span className="required-indicator">*</span>}
            </label>
        );
    };
  
  return (
    <div className={`form-group ${hasError ? 'has-error' : ''} ${showCheckingState ? 'checking-email' : ''}`}>
      <label htmlFor={id} className={required ? 'required' : ''}>
            {renderLabel()}
      </label>
      <div className="input-wrapper">
        <input
          type="email"
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          autoComplete={autocomplete}
          className={`${hasError ? 'error' : ''} ${showCheckingState ? 'checking' : ''}`}
          required={required}
        />
        {showCheckingState && (
          <div className="email-checking-indicator">
            <i className="icon-spin animate-spin"></i>
          </div>
        )}
      </div>
      {hasError && (
        <div className={`error-msg ${isCheckingFromParent ? 'checking-msg' : ''}`}>
          <i className={isCheckingFromParent ? "icon-spin animate-spin" : "icon-attention"}></i> {errorMessage}
        </div>
      )}
    </div>
  );
};

export default EmailInput; 